import { demoMorningCheck } from "./demoData";
import type {
  AiConsumptionRow,
  AiQuotaConfig,
  ClientDetailPayloadMap,
  ClientDetailSection,
  ClientHealth,
  MorningCheckPayload,
  SessionPayload,
  GertrudisAuditPayload,
  GertrudisUsedData,
  SeoAuditSnapshot,
  SemrushExplorePayload,
  TransparencyDetailPayload,
  UrlDetailPayload,
  UsagePayload,
  GtmCompliancePayload,
  ClientObjective,
  ClientObjectiveRoasRow,
  GertrudisAgentRow,
  VaultTelemetryPayload,
  VaultRulesPayload,
  HitlFeedbackRow,
  HitlFeedbackResponse,
  GertrudisStructuredRule,
  TriageFeedbackPayload,
  PromoteRulePayload,
  PromptAssemblyPayload,
  BotMetadata,
  ConversationOverviewPayload,
  AutomationsHealthPayload,
  BudgetPlanPayload,
  BudgetPlanSavePayload,
  CostoSavePayload,
  DolarHoy,
  CostosPayload,
  CostosUnificado,
  SocialOverviewPayload,
  SocialQueuePayload,
  SocialReplySuggestPayload,
  SocialReplySuggestResponse,
  SocialReplyPublishPayload,
  SocialReplyPublishResponse,
  SocialMensajesPayload,
  SocialYoutubePayload,
  SocialMensajesListaPayload,
  SocialMensajesFiltro,
  SocialMensajesHilo,
  SocialMensajesPanorama,
} from "./types";

const explicitDemoMode = import.meta.env.VITE_WOLF_REACT_DEMO === "true";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...init?.headers },
    ...init,
  });

  if (response.status === 401) {
    if (window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
    throw new Error("Sesión requerida");
  }
  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `Error ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export interface LoginResponse {
  success: boolean;
  user?: {
    email: string;
    name: string;
    client_slug: string;
    role: string;
  };
  redirect_url?: string;
}

export async function loginWithPassword(email: string, password: string): Promise<LoginResponse> {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const text = await response.text();
    let detail = "Error al iniciar sesión";
    try {
      const parsed = JSON.parse(text);
      if (parsed.detail) detail = parsed.detail;
    } catch {
      // no es json
    }
    throw new Error(detail);
  }
  return response.json() as Promise<LoginResponse>;
}

export async function fetchSession(): Promise<SessionPayload> {
  try {
    return await request<SessionPayload>("/api/session");
  } catch (error) {
    if (import.meta.env.DEV || explicitDemoMode) {
      return {
        authenticated: true,
        email: "local@wolfagency.cl",
        name: "Wolf Local",
        canViewUsage: true,
        canViewObjectiveRoas: true,
        canUploadSemrush: true,
        canEditBudgetPlan: true,
        canViewCosts: true,
        canViewGertrudis: true,
        canViewNotebook: true,
        isAdmin: true,
        canReplySocial: true,
        authMode: "bypass",
      };
    }
    throw error;
  }
}

export async function fetchMorningCheck(): Promise<MorningCheckPayload> {
  try {
    return await request<MorningCheckPayload>("/api/morning-check");
  } catch (error) {
    if (explicitDemoMode) return demoMorningCheck;
    throw error;
  }
}

export async function fetchClientHealth(clientSlug: string): Promise<ClientHealth> {
  try {
    return await request<ClientHealth>(`/api/client-health/${encodeURIComponent(clientSlug)}`);
  } catch (error) {
    if (explicitDemoMode) {
      return (
        demoMorningCheck.health[clientSlug] ?? {
          gscStatus: "NO_APLICA",
          gscDays: null,
          ga4Status: "NO_APLICA",
          ga4Days: null,
          paidStatus: "NO_APLICA",
          paidPlatform: null,
          paidDays: null,
        }
      );
    }
    throw error;
  }
}

export async function fetchAiConsumption(days: number): Promise<AiConsumptionRow[]> {
  try {
    return await request<AiConsumptionRow[]>(`/api/ai-consumption?days=${days}`);
  } catch (error) {
    if (explicitDemoMode) {
      return [
        {
          modelo: "models/gemini-2.5-flash",
          peticiones_ultimo_min: 4,
          tokens_ultimo_min: 84712,
          peticiones_hoy: 93,
          ultima_peticion: new Date().toISOString(),
        },
        {
          modelo: "claude-sonnet-4-6",
          peticiones_ultimo_min: 1,
          tokens_ultimo_min: 12840,
          peticiones_hoy: 18,
          ultima_peticion: new Date(Date.now() - 1_200_000).toISOString(),
        },
      ];
    }
    throw error;
  }
}

export async function fetchAiQuotaConfig(): Promise<AiQuotaConfig> {
  try {
    return await request<AiQuotaConfig>("/api/ai-quota-config");
  } catch (error) {
    if (explicitDemoMode) {
      return {
        geminiPlans: {
          Gratuito: { "gemini-2.5-flash": { RPM: 10, TPM: 250000, RPD: 500 } },
          "Pago por uso": { "gemini-2.5-flash": { RPM: 2000, TPM: 4000000, RPD: null } },
        },
        claude: { "claude-sonnet-4-6": { RPM: 50, TPM: 30000, RPD: null } },
        activeProvider: "GEMINI",
        safetyPct: 80,
      };
    }
    throw error;
  }
}

export async function fetchUsage(days: number): Promise<UsagePayload> {
  try {
    return await request<UsagePayload>(`/api/usage?days=${days}`);
  } catch (error) {
    if (explicitDemoMode) {
      const now = new Date().toISOString();
      return {
        kpis: { users: 9, sessions: 34, events: 286, page_views: 177 },
        funnel: { attempts: 28, attempts_total: 31, blocked: 3, invoked: 28 },
        by_user: [
          {
            user_email: "local@wolfagency.cl",
            eventos: 91,
            sesiones: 24,
            ultima_actividad: now,
            intentos_gertrudis: 9,
            llamadas_gertrudis: 6,
            cooldowns_gertrudis: 0,
          },
        ],
        by_page: [
          { page: "morning-check", vistas: 82, sesiones: 24 },
          { page: "cliente", vistas: 61, sesiones: 18 },
          { page: "gemini", vistas: 34, sesiones: 11 },
        ],
        revisits: [
          { page: "cliente", client_slug: "cliente-demo", vistas: 12, sesiones: 5 },
        ],
        recent: [
          {
            timestamp: now,
            user_email: "local@wolfagency.cl",
            event_type: "page_view",
            page: "uso",
            client_slug: null,
            detail: null,
          },
        ],
      };
    }
    throw error;
  }
}

const demoAudit = {
  meta: { site: "Afex T&C", url: "afex.cl", periodStart: "2026-05-01", periodEnd: "2026-07-29" },
  kpis: { clics: 18742, impresiones: 812430, ctr: 2.31, posicionPromedio: 11.8, srTotalKW: 1260, totalLeads: 486 },
  srKeywords: [
    { kw: "envío de dinero", pos: 4, volume: 9900, traffic: 742, url: "afex.cl/envios" },
    { kw: "casa de cambio", pos: 8, volume: 6600, traffic: 318, url: "afex.cl" },
  ],
  srCompetitors: [
    { domain: "westernunion.com", relevance: 0.84, commonKw: 392, orgKw: 5820, orgTraffic: 98100 },
    { domain: "global66.com", relevance: 0.69, commonKw: 214, orgKw: 3140, orgTraffic: 62400 },
  ],
  conversion: { totalLeads: 486, channels: [{ canal: "Orgánico", leads: 301 }, { canal: "Paid", leads: 185 }] },
  topConvertingPages: [{ pagina: "/envios", sesiones: 4820, leads: 214, cr: 0.044 }],
  backlinks: {
    total: 12840,
    uniqueDomains: 684,
    dofollow: 10720,
    nofollow: 2120,
    spam: 12,
    _disponible: true,
    byAS: { "0-10": 142, "11-20": 196, "21-30": 171, "31-40": 102, "41+": 73 },
    topAnchors: [{ anchor: "afex", count: 1430 }, { anchor: "envíos internacionales", count: 816 }],
    topDomains: [{ domain: "latercera.com", count: 42, as: 78 }, { domain: "emol.com", count: 31, as: 75 }],
  },
  authority: [
    { domain: "afex.cl", as: 46, domRef: 684, backlinks: 12840, ips: 492, note: "Dominio propio", isUs: true },
    { domain: "westernunion.com", as: 76, domRef: 19040, backlinks: 1480000, ips: 8420, note: "Competidor", isUs: false },
  ],
};

const demoFindings = [
  { id: "F001", icon: "🔴", title: "CTR por debajo del potencial", desc: "Las URLs con mayor volumen todavía dejan demanda orgánica sin capturar.", priority: "high", tag: "On-page", sentido: "oportunidad" },
  { id: "F002", icon: "🟢", title: "Cobertura orgánica sólida", desc: "El dominio mantiene presencia relevante para búsquedas transaccionales del negocio.", priority: "medium", tag: "Contenido", sentido: "fortaleza" },
];

const demoActions = [
  { id: "A001", title: "Reescribir titles y descriptions de las URLs con más impresiones y bajo CTR", category: "On-page", priority: "high", impact: "Capturar más clics con el ranking actual" },
  { id: "A002", title: "Agregar enlaces internos hacia las keywords en striking distance", category: "Contenido", priority: "medium", impact: "Empujar términos de página 2 a Top 10" },
];

const demoGertrudis: GertrudisAuditPayload = {
  id: "demo-audit-latest",
  insight: "🔴 Diagnóstico: la URL concentra impresiones, pero el CTR todavía está bajo para su posición.\n💡 Insight Wolf: existe demanda validada; el cuello de botella está en el snippet y la alineación de intención.\n🛠️ Acción: reescribe title y meta description, agrega una respuesta directa arriba del fold y mide el cambio durante 14 días.",
  timestamp: new Date(Date.now() - 86_400_000).toISOString(),
  actions: [
    "Reescribir title y meta description de la landing prioritaria.",
    "Agregar una respuesta directa y medir el cambio durante 14 días.",
  ],
};

const demoGertrudisPrevious: GertrudisAuditPayload = {
  id: "demo-audit-previous",
  insight: "🟡 Diagnóstico anterior: la landing ya tenía demanda orgánica, pero todavía no concentraba la respuesta principal arriba del fold.\n🛠️ Acción: ordenar el contenido y reforzar el enlazado interno hacia esta URL.",
  timestamp: new Date(Date.now() - 21 * 86_400_000).toISOString(),
  actions: ["Ordenar el contenido principal.", "Reforzar el enlazado interno hacia esta landing."],
};

function demoClientDetail<S extends ClientDetailSection>(section: S): ClientDetailPayloadMap[S] {
  const today = new Date().toISOString().slice(0, 10);
  const sections: ClientDetailPayloadMap = {
    seo: {
      window: { window: "MTD", period_start: "2026-08-01", period_end: today, days_total: 4 },
      trend: [
        { date: "2026-08-01", clics: 628, impresiones: 28200 },
        { date: "2026-08-02", clics: 704, impresiones: 30100 },
        { date: "2026-08-03", clics: 681, impresiones: 29540 },
      ],
      strikingDistance: [{ palabra_clave: "transferencias internacionales", posicion: 12.4, impresiones: 4980, clics: 56, url_clean: "afex.cl/envios" }],
      cannibalization: [{ palabra_clave: "cambio dolar", tipo_canibalizacion: "PARCIAL", impresiones_totales: 3840, url_dominante: "afex.cl/dolar", url_canibalizada: "afex.cl/tipo-cambio" }],
      geo: [{ url_clean: "afex.cl/envios", Clics: 418, Schema_Detectado: null }],
      brokenIntent: [{ url_clean: "afex.cl/sucursales", clics_seo: 284, ctr_seo: 0.018, engagement_rate_real: 0.24 }],
      lcp: [{ url_clean: "afex.cl", lcp_seconds: 3.8, performance_score: 54 }],
      lowCtr: [{ url_clean: "afex.cl/dolar", impresiones: 16400, clics: 118, ctr: 0.0072 }],
      decliningTraffic: [{ url_clean: "afex.cl/cambio", sesiones_actual: 930, sesiones_anterior: 1260, variacion_pct: -26.2 }],
      movements: [{ url_clean: "afex.cl/envios", palabra_clave: "envíos al extranjero", posicion_actual: 5.2, posicion_anterior: 8.6, delta_posicion: 3.4 }],
      devices: [{ device: "mobile", sesiones: 12840, engagement_rate: 0.46 }, { device: "desktop", sesiones: 6210, engagement_rate: 0.58 }],
      urls: [{ url_clean: "afex.cl/envios" }, { url_clean: "afex.cl/dolar" }, { url_clean: "afex.cl/sucursales" }],
      findings: demoFindings,
      actions: demoActions,
      findingsSnapshot: "2026-08-05",
      auditHistory: [
        { snapshot: "2026-08-05", findings: demoFindings, actions: demoActions },
        { snapshot: "2026-07-20", findings: demoFindings.slice(0, 1), actions: demoActions.slice(0, 1) },
      ],
      audit: demoAudit,
      siteAuditIssues: [{ issue_type: "Error", issue_name: "404 Page", issues_count: 5 }],
      siteAuditPages: [{ page_url: "afex.cl/404", issues_count: 1 }],
      semrushCompetitors: [{ domain: "global66.com", relevance: 0.82, commonKw: 1240, orgKw: 18400, orgTraffic: 420000 }],
    },
    paid: {
      window: { window: "MTD", period_start: "2026-08-01", period_end: today, days_total: 4 },
      pacing: [{ platform: "meta", account_id: "afex-meta", account_name: "Afex", spend_mtd: 2862255, spend_daily_mtd: 418000, budget_month_est: 15600000, conversions_mtd: 318, conversion_value_mtd: 7230000, status: "AT_RISK" }],
      daily: [{ date: "2026-08-01", platform: "meta", account_name: "Afex", spend_daily: 402000, budget_daily: 520000, conversions_daily: 82 }, { date: "2026-08-02", platform: "meta", account_name: "Afex", spend_daily: 448000, budget_daily: 520000, conversions_daily: 91 }],
      lifetime: [{ platform: "meta", campaign_name: "WD - Apoyo Venezuela - Alcance - Jun26", spend_to_date: 150000, lifetime_budget: 150000, pct_consumed: 1, status: "OVER" }],
      cpc: [{ date: "2026-08-01", clics: 1240, costo_total: 402000, cpc_dia: 324 }],
      params: { ROAS_MIN: "2.5", CPA_MAX: "15000" },
      gertrudisAudits: [{ ...demoGertrudis, history: [demoGertrudis, demoGertrudisPrevious], level: "Per-campaña Paid", element: "meta:afex-meta", platform: "meta", accountId: "afex-meta" }],
      summary: { spend_total: 2862255, spend_daily: 2712255, spend_lifetime: 150000, conversions: 318, conversion_value: 7230000, cpa: 9001, roas: 2.53 },
      previous: { spend_total: 2510000, conversions: 287, conversion_value: 6810000, cpa: 8745, roas: 2.71 },
      organicTraffic: 19050,
      organicPrevious: 17680,
      dailyCampaigns: [{ platform: "meta", account_id: "afex-meta", campaign_name: "Tráfico · Cyber", spend_mtd: 345977, budget_month_est: 350000, pct_used_mtd: .989, forecast_eom: 364000, status: "AT_RISK" }],
      gaps: [{ palabra_clave: "dólar hoy", Pos_Organica: 2.4, Costo_Mensual_Est: 284000, decision: "PAUSAR_PAID" }],
    },
    transparency: {
      ads: [{ advertiser_legal_name: "WOLF ASESORES SPA", advertiser_disclosed_name: "Afex", advertiser_location: "CL", advertiser_verification_status: "VERIFIED", creative_id: "CR-982140", ad_format_type: "TEXT", topic: "Financial services", creative_page_url: "https://adstransparency.google.com/advertiser/AR12019259956864745473", snapshot_date: today, is_fallback: false }],
      gscUrls: ["https://afex.cl/"],
    },
    gaps: {
      rows: [{ palabra_clave: "dólar hoy", url_clean: "afex.cl/dolar", Pos_Organica: 2.4, Sesiones_Org_30d: 1280, Costo_Mensual_Est: 284000, decision: "PAUSAR_PAID" }, { palabra_clave: "envíos internacionales", url_clean: "afex.cl/envios", Pos_Organica: 5.1, Sesiones_Org_30d: 810, Costo_Mensual_Est: 192000, decision: "EVALUAR" }],
    },
    audit: { audit: demoAudit },
    social: {
      summary: [
        { platform: "meta_fb", platform_group: "meta", origin: "paid", total_comments: 420, root_comments: 310, replies: 110, unique_authors: 280, unique_messages: 395, avg_message_length: 54 },
        { platform: "meta_fb", platform_group: "meta", origin: "organic", total_comments: 115, root_comments: 85, replies: 30, unique_authors: 92, unique_messages: 110, avg_message_length: 48 },
        { platform: "meta_ig", platform_group: "meta", origin: "paid", total_comments: 861, root_comments: 650, replies: 211, unique_authors: 512, unique_messages: 810, avg_message_length: 62 },
        { platform: "meta_ig", platform_group: "meta", origin: "organic", total_comments: 170, root_comments: 130, replies: 40, unique_authors: 140, unique_messages: 162, avg_message_length: 51 },
      ],
      recentComments: [
        { date: today, platform: "meta_ig", origin: "paid", feedback_type: "comment", author_name: "Paciente Ejemplo", message: "Hola, ¿cuánto sale la evaluación dental en la sede de Providencia?", created_time: `${today}T12:30:00Z` },
        { date: today, platform: "meta_fb", origin: "paid", feedback_type: "comment", author_name: "Claudio Morales", message: "Excelente atención del Dr. Mora, muy profesional y dedicado.", created_time: `${today}T11:15:00Z` },
      ],
      audit: {
        ...demoGertrudis,
        history: [demoGertrudis, demoGertrudisPrevious],
        level: "Per-cliente Social",
        element: "demo",
      },
    },
    audiencias: { client_slug: "demo", canales: [], videos: [] },
    whatsapp: {
      has_data: true,
      client_slug: "cliente-demo",
      ga4_property_id: "377741356",
      history: [
        { mes: "2026-05", total_clics: 40, usuarios: 37, clics_per_user: 1.08, gads_conversions: 0, paid_share_pct: 0, is_complete: false, is_current: false, note: "Parcial (desde 22-may)" },
        { mes: "2026-06", total_clics: 154, usuarios: 129, clics_per_user: 1.19, gads_conversions: 6.88, paid_share_pct: 4.5, is_complete: true, is_current: false, note: "Mes completo" },
        { mes: "2026-07", total_clics: 282, usuarios: 219, clics_per_user: 1.29, gads_conversions: 185.34, paid_share_pct: 65.7, is_complete: true, is_current: false, note: "Mes completo" },
        { mes: "2026-08", total_clics: 453, usuarios: 321, clics_per_user: 1.41, gads_conversions: 356.19, paid_share_pct: 78.6, is_complete: true, is_current: false, note: "Mes completo" },
        { mes: "2026-09", total_clics: 718, usuarios: 356, clics_per_user: 2.02, gads_conversions: 652.75, paid_share_pct: 90.9, is_complete: false, is_current: true, note: "En curso (parcial)" },
      ],
      stats: {
        complete_months: 3,
        avg_clicks: 296.3,
        median_clicks: 282.0,
        p75_clicks: 453.0,
        current_month_clicks: 718,
        current_month_projected: 898,
      },
      scenarios: {
        conservative: { name: "Conservador (Piso Histórico)", clicks: 280, description: "Mediana de meses cerrados (junio - agosto), sin sobreponderar pauta." },
        base: { name: "Base (Ritmo Actual)", clicks: 900, description: "Proyección mensual de septiembre a ritmo constante de Google Ads." },
        high: { name: "Alto (Pico Q4 / Verano)", clicks: 1300, description: "Escalamiento por estacionalidad de arriendo y fin de año." }
      },
      management: {
        status: "ESPERANDO_LANZAMIENTO",
        status_label: "Lanzamiento Programado (Próxima Semana)",
        bot_number: "Pendiente de activación",
        contacts_today: 0,
        contacts_mtd: 0,
        last_contact_at: null,
        attribution_records: []
      }
    },
    "semrush-explore": {
      keywordGap: [{ kw: "enviar dinero a peru", volume: 1400, kd: 45, source: "global66.com", position: 2 }],
      backlinksAtomic: [{ source_title: "Transferencias internacionales", source_url: "example.com/blog", target_url_clean: "afex.cl/envios", anchor: "afex chile", page_ascore: 24 }]
    }
  };
  return sections[section];
}

export async function fetchUrlDetail(clientSlug: string, url: string, window = "MTD"): Promise<UrlDetailPayload> {
  try {
    return await request<UrlDetailPayload>(
      `/api/url-detail/${encodeURIComponent(clientSlug)}?url=${encodeURIComponent(url)}&window=${encodeURIComponent(window)}`,
    );
  } catch (error) {
    if (explicitDemoMode) {
      return {
        url,
        window: { window, period_start: "2026-08-01", period_end: new Date().toISOString().slice(0, 10), days_total: 30 },
        radiography: { funnel_type: "BoF", lcp_seconds: 3.1, cls_score: 0.08, performance_score: 68, geo_ready_flag: false, schema_types: "WebPage", intencion_rota_flag: true },
        kpis: { sesiones_totales_30d: 4820, engagement_rate_avg: 0.41, clics_totales_30d: 742, ctr_total_avg: 0.021, impresiones_totales_30d: 35300, posicion_promedio: 7.4 },
        keywords: [{ palabra_clave: "envío de dinero", tipo_busqueda: "TRANSACCIONAL", impresiones: 9900, clics: 318, ctr: 0.032, posicion: 4.2 }],
        evolution: [{ date: "2026-08-01", clics: 92, impresiones: 4310 }, { date: "2026-08-02", clics: 104, impresiones: 4680 }, { date: "2026-08-03", clics: 98, impresiones: 4510 }],
        devices: [{ device: "mobile", sesiones: 3210, clics: 492, engagement_rate: 0.37 }, { device: "desktop", sesiones: 1610, clics: 250, engagement_rate: 0.49 }],
        latestAudit: { ...demoGertrudis, history: [demoGertrudis, demoGertrudisPrevious], level: "Per-URL SEO", element: url },
        laboratory: [{ id_experimento: "EXP-21", plataforma: "SEO", regla_testeada: "Nuevo snippet transaccional", estado: "EN_MEDICION", dias_transcurridos: 6, dias_restantes: 8 }],
        cannibalization: [{ palabra_clave: "cambio dolar", tipo_canibalizacion: "PARCIAL", url_dominante: url, url_canibalizada: "afex.cl/tipo-cambio", impresiones_totales: 3840 }],
        geo: [{ url_clean: url, Clics: 418, Schema_Detectado: null }],
        commands: { fase_1_gsc: `gcloud run jobs execute job-ingest-gsc --args='--client,${clientSlug}' --wait`, fase_2_enrich: `gcloud run jobs execute job-enrich-seo --args='--client,${clientSlug}' --wait`, nota: "Ejecutar Fase 1 y luego Fase 2." },
      };
    }
    throw error;
  }
}

export async function runGertrudisSeo(clientSlug: string, url: string, window = "MTD"): Promise<GertrudisAuditPayload> {
  try {
    return await request<GertrudisAuditPayload>("/api/gertrudis/seo", {
      method: "POST",
      body: JSON.stringify({ clientSlug, url, window }),
    });
  } catch (error) {
    if (explicitDemoMode) return { ...demoGertrudis, id: null, timestamp: new Date().toISOString(), level: "Per-URL SEO", element: url };
    throw error;
  }
}

export async function runGertrudisPaid(clientSlug: string, platform: string, accountId?: string): Promise<GertrudisAuditPayload> {
  try {
    return await request<GertrudisAuditPayload>("/api/gertrudis/paid", {
      method: "POST",
      body: JSON.stringify({ clientSlug, platform, accountId: accountId || null }),
    });
  } catch (error) {
    if (explicitDemoMode) return { ...demoGertrudis, id: null, timestamp: new Date().toISOString(), level: "Per-campaña Paid", element: `${platform}:${accountId ?? ""}` };
    throw error;
  }
}

export async function runGertrudisSocial(clientSlug: string): Promise<GertrudisAuditPayload> {
  try {
    return await request<GertrudisAuditPayload>("/api/gertrudis/social", {
      method: "POST",
      body: JSON.stringify({ clientSlug }),
    });
  } catch (error) {
    if (explicitDemoMode) return { ...demoGertrudis, id: null, timestamp: new Date().toISOString(), level: "Per-cliente Social", element: clientSlug };
    throw error;
  }
}

export async function runPageSpeed(clientSlug: string, url: string): Promise<{ ok: boolean; message: string }> {
  if (explicitDemoMode) return { ok: true, message: "Auditoría en proceso. Refresca en ~30s para ver los resultados." };
  return request("/api/pagespeed", {
    method: "POST",
    body: JSON.stringify({ clientSlug, url }),
  });
}

export interface SeoTecnicoLighthouse {
  fecha: string;
  performance: number | null;
  accesibilidad: number | null;
  buenas_practicas: number | null;
  seo: number | null;
  lcp_s: number | null;
  cls: number | null;
}

export interface SeoTecnicoPlantilla {
  plantilla: string;
  url: string;
  paginas: number | null;
  lighthouse: SeoTecnicoLighthouse | null;
}

export interface SeoTecnicoVersion {
  version: number;
  created_at: string;
  created_by: string;
  base_version: number | null;
  instruccion: string | null;
  modelo: string;
  alertas: string | null;
}

export interface SeoTecnicoEstado {
  sitio: string | null;
  fuentes: {
    megaexport: { fecha: string | null; urls: number };
    issues: { fecha: string | null };
    lighthouse: { fecha: string | null; urls: number };
  };
  plantillas: SeoTecnicoPlantilla[];
  informes: SeoTecnicoVersion[];
}

const seoTecnicoBase = (clientSlug: string) => `/api/seo/tecnico/${encodeURIComponent(clientSlug)}`;

export function fetchSeoTecnico(clientSlug: string): Promise<SeoTecnicoEstado> {
  return request(seoTecnicoBase(clientSlug));
}

export function runLighthousePlantillas(
  clientSlug: string,
  items: { plantilla: string; url: string }[],
): Promise<{ ok: number; errores: { url: string; plantilla: string; error: string }[] }> {
  return request(`${seoTecnicoBase(clientSlug)}/lighthouse`, { method: "POST", body: JSON.stringify({ items }) });
}

export function generarInformeSeoTecnico(
  clientSlug: string,
  ajuste?: { instruccion: string; baseVersion: number },
): Promise<{ version: number; modelo: string; alertas: string | null }> {
  return request(`${seoTecnicoBase(clientSlug)}/informe`, { method: "POST", body: JSON.stringify(ajuste ?? {}) });
}

export function urlInformeSeoTecnico(clientSlug: string, version: number, descargar = false): string {
  return `${seoTecnicoBase(clientSlug)}/informe/${version}${descargar ? "?descargar=true" : ""}`;
}

export async function fetchInformeSeoTecnicoHtml(clientSlug: string, version: number): Promise<string> {
  const response = await fetch(urlInformeSeoTecnico(clientSlug, version), { credentials: "include" });
  if (!response.ok) throw new Error((await response.text()) || `Error ${response.status}`);
  return response.text();
}

export async function syncGeoTracking(): Promise<{
  ok: boolean;
  before: { total: number; afex: number; wolf: number };
  after: { total: number; afex: number; wolf: number };
  filas_agregadas: number;
}> {
  return request("/api/seo/geo-tracking/sync", {
    method: "POST",
  });
}

export async function fetchGeoTracking(clientSlug: string): Promise<any> {
  return request(`/api/seo/geo-tracking/${encodeURIComponent(clientSlug)}`);
}


export async function submitGertrudisFeedback(input: {
  clientSlug: string;
  insight: string;
  rating: "POSITIVO" | "NEGATIVO";
  comment?: string;
  domain: "SEO" | "MKT";
  auditType: string;
  level?: string;
  element?: string;
}): Promise<{ ok: boolean; id: string }> {
  if (explicitDemoMode) return { ok: true, id: crypto.randomUUID() };
  return request("/api/gertrudis/feedback", { method: "POST", body: JSON.stringify(input) });
}

export async function triggerGertrudisSeoAudit(clientSlug: string): Promise<{
  success: boolean;
  snapshot: string;
  findings: Record<string, any>[];
  actions: Record<string, any>[];
  auditHistory: SeoAuditSnapshot[];
  usedData?: GertrudisUsedData;
}> {
  return request(`/api/seo/audit/generate/${encodeURIComponent(clientSlug)}`, {
    method: "POST",
  });
}

export async function downloadSeoAudit(clientSlug: string): Promise<void> {
  if (explicitDemoMode) {
    const demoHtml = `<!doctype html><html lang="es"><meta charset="utf-8"><title>Auditoría SEO ${clientSlug}</title><body><h1>Auditoría SEO ${clientSlug}</h1><p>Entregable de demostración.</p></body></html>`;
    const url = URL.createObjectURL(new Blob([demoHtml], { type: "text/html" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `auditoria_seo_${clientSlug}.html`;
    anchor.click();
    URL.revokeObjectURL(url);
    return;
  }
  const response = await fetch(`/api/audit-html/${encodeURIComponent(clientSlug)}`, {
    method: "POST",
    credentials: "include",
  });
  if (response.status === 401) {
    if (window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
    throw new Error("Sesión requerida");
  }
  if (!response.ok) throw new Error(await response.text() || `Error ${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `auditoria_seo_${clientSlug}.html`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function fetchClientDetail<S extends ClientDetailSection>(
  clientSlug: string,
  section: S,
  window = "MTD",
): Promise<ClientDetailPayloadMap[S]> {
  try {
    return await request<ClientDetailPayloadMap[S]>(
      `/api/client-detail/${encodeURIComponent(clientSlug)}/${section}?window=${encodeURIComponent(window)}`,
    );
  } catch (error) {
    if (explicitDemoMode) return demoClientDetail(section);
    throw error;
  }
}

export interface SemrushClientOption {
  clientSlug: string;
  displayName: string;
  domain: string | null;
}

export interface SemrushUploadResult {
  ok: boolean;
  rowsLoaded: number;
  clientSlug: string;
  domain: string;
  snapshotDate: string;
}

export type SemrushReportType =
  | "keywords"
  | "backlinks"
  | "site-audit-issues"
  | "site-audit-pages"
  | "site-audit-page-issues"
  | "competitors"
  | "keyword-gap";

export async function fetchSemrushClients(): Promise<SemrushClientOption[]> {
  return request<SemrushClientOption[]>("/api/semrush/clients");
}

export async function uploadSemrushReport(
  reportType: SemrushReportType,
  fields: Record<string, string>,
  file: File,
): Promise<SemrushUploadResult> {
  const body = new FormData();
  body.append("file", file);
  Object.entries(fields).forEach(([key, value]) => {
    if (value) body.append(key, value);
  });
  const response = await fetch(`/api/semrush/upload-${reportType}`, {
    method: "POST",
    credentials: "include",
    body,
  });
  if (response.status === 401) {
    if (window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
    throw new Error("Sesión requerida");
  }
  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      detail = JSON.parse(text).detail ?? text;
    } catch {
      // el body no era JSON, se usa el texto crudo
    }
    throw new Error(detail || `Error ${response.status}`);
  }
  return response.json() as Promise<SemrushUploadResult>;
}

export interface SemrushCoverageItem {
  reportType: string;
  label: string;
  lastSnapshot: string | null;
  daysAgo: number | null;
  status: "FRESH" | "WARNING" | "STALE" | "NO_DATA" | "ERROR";
  totalRows: number;
  latestRows: number;
  hasData: boolean;
}

export interface SemrushOverviewPayload {
  clientSlug: string;
  visibilidadShare: Array<{
    snapshot_date: string;
    kw_top3: number;
    kw_top10: number;
    kw_top20: number;
    kw_mas_20: number;
    kw_total: number;
    traffic_total_est: number | null;
  }>;
  siteHealthTrend: Array<{
    snapshot_date: string;
    health_score: number | null;
    errors_count_agregado: number | null;
    warnings_count_agregado: number | null;
    notices_count: number | null;
    pages_crawled: number | null;
    errors_failed_checks: number | null;
    warnings_failed_checks: number | null;
    notices_failed_checks: number | null;
  }>;
  authorityTrend: Array<{
    snapshot_date: string;
    ascore: number | null;
    total_backlinks: number | null;
    domains_num: number | null;
    follows_num: number | null;
    nofollows_num: number | null;
  }>;
  siteAuditIssues: Array<{
    issue_id: number;
    issue_type: string;
    issue_name: string;
    failed_checks: number;
    total_checks: number;
    changed_from_last_audit: number | null;
    snapshot_date: string;
  }>;
  siteAuditPages: Array<{
    url_clean: string;
    page_title: string | null;
    http_status_code: number | null;
    load_time_sec: number | null;
    issues_count: number;
    canonicalization: string | null;
    has_schema_microdata: boolean | null;
    has_schema_jsonld: boolean | null;
    has_open_graph: boolean | null;
    in_sitemap: boolean | null;
  }>;
  siteAuditPageIssues?: Array<{
    url_clean: string;
    url_raw?: string;
    issue_name: string;
    issue_count: number;
    issue_type?: string | null;
    snapshot_date?: string;
  }>;
}

export async function fetchSemrushCoverageStatus(clientSlug: string): Promise<SemrushCoverageItem[]> {
  return request<SemrushCoverageItem[]>(`/api/semrush/status/${encodeURIComponent(clientSlug)}`);
}

export async function fetchSemrushOverview(clientSlug: string): Promise<SemrushOverviewPayload> {
  return request<SemrushOverviewPayload>(`/api/client/${encodeURIComponent(clientSlug)}/semrush/overview`);
}

export async function fetchSemrushExplore(clientSlug: string): Promise<any> {
  return request<any>(`/api/client/${encodeURIComponent(clientSlug)}/semrush/explore`);
}

export async function fetchSemrushPageIssues(clientSlug: string): Promise<any[]> {
  return request<any[]>(`/api/client/${encodeURIComponent(clientSlug)}/semrush/page-issues`);
}

export function getAuditExportUrl(clientSlug: string, preview = false): string {
  return `/api/client/${encodeURIComponent(clientSlug)}/audit/export-html${preview ? "?preview=true" : ""}`;
}

/* ── Reporte de cuenta (GA4 + Google Ads + Meta) ───────────────────────────── */

export type ReportCoverageState = "REAL" | "PARCIAL" | "SIN_DATOS";

export interface ReportCoverage {
  state: ReportCoverageState;
  reason: string | null;
}

export interface ReportUnavailable {
  metric: string;
  reason: string;
}

export interface ReportChartSeries {
  id: string;
  label: string;
  format: "money" | "integer" | "percent" | "ratio";
  values: number[];
}

export interface ReportChart {
  title: string;
  chartType: "line" | "bar" | "combo" | "area";
  dimension: { id: string; label: string };
  labels: string[];
  series: ReportChartSeries[];
  days: number;
  platform: string | null;
  coverage: ReportCoverage;
  source: string;
}

export interface ReportTheme {
  primary: string;
  accent: string;
  surface: string;
}

export interface ReportRow {
  [key: string]: string | number | null;
}

export interface ReportGa4Block {
  coverage: ReportCoverage;
  totals: Record<string, number | null>;
  unavailable: ReportUnavailable[];
  sources: ReportRow[];
  weekdays: ReportRow[];
  monthly: ReportRow[];
  daily: ReportRow[];
  range: { from: string | null; to: string | null };
}

export interface ReportPaidBlock {
  coverage: ReportCoverage;
  totals: Record<string, number | null>;
  reliability: { roasReliable: boolean; cpaReliable: boolean; reasons: string[] };
  freshness: { lastDate: string | null; lagDays: number | null };
  campaigns: ReportRow[];
  monthly: ReportRow[];
  daily: ReportRow[];
  range: { from: string | null; to: string | null };
  keywords?: { rows: ReportRow[]; coverage: ReportCoverage };
  searchTerms?: { rows: ReportRow[]; coverage: ReportCoverage };
  ads?: { rows: ReportRow[]; coverage: ReportCoverage; unavailable: ReportUnavailable[] };
}

export interface ReportOverview {
  clientSlug: string;
  days: number;
  window: { from: string; to: string };
  theme: ReportTheme;
  consolidated: {
    inversionTotal: number;
    ingresosTotales: number;
    roasGlobal: number | null;
    roasWindow: string | null;
    note: string | null;
    stalePlatforms: string[];
  };
  ga4: ReportGa4Block;
  gads: ReportPaidBlock;
  meta: ReportPaidBlock;
}

export interface ReportChartSpec {
  metrics: string[];
  dimension?: string;
  days?: number;
  platform?: string | null;
  limit?: number | null;
  chartType?: string;
  title?: string;
}

export interface ReportAnalystResponse {
  insight: string;
  actions: string[];
  chart: ReportChart | null;
  theme: ReportTheme | null;
  notices: string[];
  timestamp: string;
  level: string;
  element: string;
}

export interface ReportLayoutResponse {
  payload: unknown | null;
  version: number;
  updatedAt: string | null;
}

export async function fetchReportLayout(layoutKey: string): Promise<ReportLayoutResponse> {
  return request<ReportLayoutResponse>(`/api/report/layout?layoutKey=${encodeURIComponent(layoutKey)}`);
}

export async function saveReportLayout(layoutKey: string, payload: unknown): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/report/layout", {
    method: "POST",
    body: JSON.stringify({ layoutKey, payload }),
  });
}

export interface ReportRegistry {
  metrics: Array<{ id: string; label: string; source: "ga4" | "paid"; format: string }>;
  dimensions: Array<{ id: string; label: string; sources: string[] }>;
  chartTypes: string[];
  days: number[];
}

export async function fetchReportRegistry(): Promise<ReportRegistry> {
  return request<ReportRegistry>("/api/report/registry");
}

export async function fetchReportOverview(clientSlug: string, days: number): Promise<ReportOverview> {
  return request<ReportOverview>(`/api/report/${encodeURIComponent(clientSlug)}/overview?days=${days}`);
}

export async function fetchReportChart(clientSlug: string, spec: ReportChartSpec): Promise<ReportChart> {
  return request<ReportChart>(`/api/report/${encodeURIComponent(clientSlug)}/chart`, {
    method: "POST",
    body: JSON.stringify(spec),
  });
}

export async function saveReportTheme(clientSlug: string, theme: Partial<ReportTheme>): Promise<ReportTheme> {
  return request<ReportTheme>(`/api/report/${encodeURIComponent(clientSlug)}/theme`, {
    method: "POST",
    body: JSON.stringify(theme),
  });
}

export async function askReportAnalyst(
  clientSlug: string,
  days: number,
  question?: string,
): Promise<ReportAnalystResponse> {
  return request<ReportAnalystResponse>(`/api/report/${encodeURIComponent(clientSlug)}/analyst`, {
    method: "POST",
    body: JSON.stringify({ days, question: question || null }),
  });
}

export function logPageView(page: string, clientSlug: string | null): void {
  void request("/api/events/page-view", {
    method: "POST",
    body: JSON.stringify({ page, clientSlug }),
  }).catch(() => undefined);
}

export async function fetchClientObjectiveRoas(): Promise<ClientObjectiveRoasRow[]> {
  try {
    return await request<ClientObjectiveRoasRow[]>("/api/client-objective-roas");
  } catch (error) {
    if (explicitDemoMode) {
      return [
        { client_slug: "fhc", display_name: "Funeraria Hogar de Cristo", objetivo_declarado: "Llamadas telefónicas", objetivo_esta_declarado: true, familia: "LEAD", portada_correcta: "Costo por llamada", roas: "No aplica" },
        { client_slug: "afex", display_name: "Afex T&C", objetivo_declarado: "Descarga de contenido", objetivo_esta_declarado: true, familia: "LEAD", portada_correcta: "Costo por descarga", roas: "No aplica" },
        { client_slug: "isigma", display_name: "Inmobiliaria SIGMA", objetivo_declarado: "Sin declarar", objetivo_esta_declarado: false, familia: null, portada_correcta: "Declarar el objetivo es la acción #1", roas: "No aplica" },
        { client_slug: "morapavic", display_name: "Mora Pavic", objetivo_declarado: "Agendamiento / reserva", objetivo_esta_declarado: true, familia: "LEAD", portada_correcta: "Costo por reserva", roas: "No aplica" },
      ];
    }
    throw error;
  }
}

export async function fetchGtmCompliance(): Promise<GtmCompliancePayload> {
  try {
    return await request<GtmCompliancePayload>("/api/compliance/gtm");
  } catch (error) {
    if (explicitDemoMode) {
      return { data: [] };
    }
    throw error;
  }
}

export async function fetchVaultAgents(): Promise<{ data: GertrudisAgentRow[] }> {
  return await request<{ data: GertrudisAgentRow[] }>("/api/vault/agents");
}

export async function fetchVaultTelemetry(days: number = 30): Promise<VaultTelemetryPayload> {
  return await request<VaultTelemetryPayload>(`/api/vault/telemetry?days=${days}`);
}

export async function fetchVaultRules(): Promise<VaultRulesPayload> {
  return await request<VaultRulesPayload>("/api/vault/rules");
}

export async function fetchVaultFeedback(params?: {
  limit?: number;
  estado?: string;
  nivel?: string;
  client_slug?: string;
  search?: string;
}): Promise<HitlFeedbackResponse> {
  const query = new URLSearchParams();
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.estado) query.set("estado", params.estado);
  if (params?.nivel) query.set("nivel", params.nivel);
  if (params?.client_slug) query.set("client_slug", params.client_slug);
  if (params?.search) query.set("search", params.search);
  const qs = query.toString();
  return await request<HitlFeedbackResponse>(`/api/vault/feedback${qs ? `?${qs}` : ""}`);
}

export async function triageFeedback(
  feedbackId: string,
  payload: TriageFeedbackPayload
): Promise<{ status: string; feedback_id: string; estado_auditoria: string }> {
  return await request<{ status: string; feedback_id: string; estado_auditoria: string }>(
    `/api/vault/feedback/${encodeURIComponent(feedbackId)}/triage`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export async function promoteRule(
  payload: PromoteRulePayload
): Promise<{ status: string; id_regla: string; titulo: string }> {
  return await request<{ status: string; id_regla: string; titulo: string }>(
    "/api/vault/rules/promote",
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function fetchStructuredRules(): Promise<{ data: GertrudisStructuredRule[] }> {
  return await request<{ data: GertrudisStructuredRule[] }>("/api/vault/rules/catalog");
}


export async function assemblePromptDryRun(
  agent: string,
  clientSlug: string,
  targetUrl?: string,
  platform?: string
): Promise<PromptAssemblyPayload> {
  return await request<PromptAssemblyPayload>("/api/vault/assemble", {
    method: "POST",
    body: JSON.stringify({
      agent,
      client_slug: clientSlug,
      target_url: targetUrl,
      platform,
    }),
  });
}

// ---------------------------------------------------------------------------
// Paid Media Endpoints (Fase 2)
// ---------------------------------------------------------------------------

export async function fetchPaidAllPacing(): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>("/api/paid/pacing/all");
}

export async function fetchClientPaidPacing(clientSlug: string): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>(`/api/client/${encodeURIComponent(clientSlug)}/paid/pacing`);
}

export async function fetchClientPaidDaily(
  clientSlug: string,
  startDate: string,
  endDate: string
): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>(
    `/api/client/${encodeURIComponent(clientSlug)}/paid/daily?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
  );
}

export async function fetchClientPaidLifetime(clientSlug: string): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>(`/api/client/${encodeURIComponent(clientSlug)}/paid/lifetime`);
}

export async function fetchClientPaidCpc(
  clientSlug: string,
  startDate?: string,
  endDate?: string,
  platform = "gads"
): Promise<Array<Record<string, unknown>>> {
  const params = new URLSearchParams();
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  params.set("platform", platform);
  return await request<Array<Record<string, unknown>>>(
    `/api/client/${encodeURIComponent(clientSlug)}/paid/cpc?${params.toString()}`
  );
}

export async function fetchClientPaidSummary(
  clientSlug: string,
  startDate: string,
  endDate: string
): Promise<Record<string, unknown>> {
  return await request<Record<string, unknown>>(
    `/api/client/${encodeURIComponent(clientSlug)}/paid/summary?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
  );
}

export async function fetchClientPaidGaps(clientSlug: string): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>(`/api/client/${encodeURIComponent(clientSlug)}/paid/gaps`);
}

export async function fetchClientPaidParams(clientSlug: string): Promise<Record<string, unknown>> {
  return await request<Record<string, unknown>>(`/api/client/${encodeURIComponent(clientSlug)}/paid/params`);
}

// ---------------------------------------------------------------------------
// Alerts & Availability Endpoints (Fase 3)
// ---------------------------------------------------------------------------

export async function fetchAlertsDaily(): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>("/api/alerts/daily");
}

export async function fetchClientAlerts(clientSlug: string): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>(`/api/alerts/client/${encodeURIComponent(clientSlug)}`);
}

export async function fetchDataAvailability(): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>("/api/alerts/availability");
}

export async function fetchClientDataHealthV2(clientSlug: string): Promise<Record<string, unknown>> {
  return await request<Record<string, unknown>>(`/api/alerts/health/${encodeURIComponent(clientSlug)}`);
}

export async function fetchPortfolioSavings(): Promise<Array<Record<string, unknown>>> {
  return await request<Array<Record<string, unknown>>>("/api/alerts/savings");
}

export async function fetchClientObjective(clientSlug: string): Promise<Record<string, unknown>> {
  return await request<Record<string, unknown>>(`/api/alerts/objectives/${encodeURIComponent(clientSlug)}`);
}

// ---------------------------------------------------------------------------
// Conversations (Wolf Bot) Endpoints
// ---------------------------------------------------------------------------

export async function fetchConversationBots(clientSlug?: string): Promise<BotMetadata[]> {
  const query = clientSlug ? `?client_slug=${encodeURIComponent(clientSlug)}` : "";
  return await request<BotMetadata[]>(`/api/conversations/bots${query}`);
}

export async function fetchConversationOverview(
  clientSlug: string,
  options?: {
    botId?: string;
    channel?: string;
    startDate?: string;
    endDate?: string;
    includeTest?: boolean;
  }
): Promise<ConversationOverviewPayload> {
  const params = new URLSearchParams();
  if (options?.botId && options.botId !== "ALL") params.set("bot_id", options.botId);
  if (options?.channel && options.channel !== "ALL") params.set("channel", options.channel);
  if (options?.startDate) params.set("start_date", options.startDate);
  if (options?.endDate) params.set("end_date", options.endDate);
  if (options?.includeTest) params.set("include_test", "true");

  const query = params.toString() ? `?${params.toString()}` : "";
  return await request<ConversationOverviewPayload>(
    `/api/client/${encodeURIComponent(clientSlug)}/conversations/overview${query}`
  );
}

export async function fetchAutomationsHealth(): Promise<AutomationsHealthPayload> {
  return await request<AutomationsHealthPayload>("/api/alerts/automations");
}

// ---------------------------------------------------------------------------
// Budget Plan (Traffic Overrides & Pacing)
// ---------------------------------------------------------------------------

export async function fetchBudgetPlan(
  clientSlug: string,
  platform: string,
  month: string,
): Promise<BudgetPlanPayload> {
  const params = new URLSearchParams({
    client_slug: clientSlug,
    platform,
    month,
  });
  return await request<BudgetPlanPayload>(`/api/budget-plan?${params.toString()}`);
}

export async function saveBudgetPlan(
  payload: BudgetPlanSavePayload,
): Promise<{ success: boolean; message: string; total_overrides?: number; dias_actualizados?: number }> {
  return await request("/api/budget-plan", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function resetBudgetPlan(
  clientSlug: string,
  platform: string,
  month: string,
  motivo: string = "Volver al reparto parejo",
): Promise<{ success: boolean; message: string }> {
  const params = new URLSearchParams({
    client_slug: clientSlug,
    platform,
    month,
    motivo,
  });
  return await request(`/api/budget-plan?${params.toString()}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Costos mensuales (VPS, RunCloud, Ads, otros)
// ---------------------------------------------------------------------------

export async function fetchCostos(month: string): Promise<CostosPayload> {
  return await request<CostosPayload>(`/api/costos?${new URLSearchParams({ month }).toString()}`);
}

export async function fetchCostosUnificado(months = 6): Promise<CostosUnificado> {
  return await request<CostosUnificado>(`/api/costos/unificado?months=${months}`);
}

export async function fetchDolarHoy(): Promise<DolarHoy> {
  return await request<DolarHoy>("/api/costos/dolar");
}

export async function saveCosto(payload: CostoSavePayload): Promise<{ success: boolean; id: string }> {
  return await request("/api/costos", { method: "PUT", body: JSON.stringify(payload) });
}

export async function deleteCosto(id: string): Promise<{ success: boolean }> {
  return await request(`/api/costos/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface SocialRangeQuery {
  desde?: string;
  hasta?: string;
}

const overviewEnCurso = new Map<string, Promise<SocialOverviewPayload>>();

/** Un pedido adelantado se entrega una sola vez, dentro de 15 s: la pestaña recibe el resumen que la página ya pidió. */
export function fetchSocialOverview(
  clientSlug: string,
  platform: string = "all",
  range: SocialRangeQuery = {},
  origin: string = "all",
): Promise<SocialOverviewPayload> {
  // `origin` solo viaja cuando se filtra, para que la petición anticipada de SocialPage acierte la misma URL.
  const params = new URLSearchParams({ platform, ...range, ...(origin === "all" ? {} : { origin }) });
  const url = `/api/social/overview/${encodeURIComponent(clientSlug)}?${params.toString()}`;
  const enCurso = overviewEnCurso.get(url);
  if (enCurso) {
    overviewEnCurso.delete(url);
    return enCurso;
  }
  const pedido = request<SocialOverviewPayload>(url);
  overviewEnCurso.set(url, pedido);
  const soltar = () => setTimeout(() => overviewEnCurso.delete(url), 15000);
  pedido.then(soltar, () => overviewEnCurso.delete(url));
  return pedido;
}

export async function fetchSocialQueue(
  clientSlug: string,
  platform: string = "all",
  range: SocialRangeQuery = {},
  origin: string = "all",
): Promise<SocialQueuePayload> {
  const params = new URLSearchParams({ platform, ...range, ...(origin === "all" ? {} : { origin }) });
  return await request<SocialQueuePayload>(
    `/api/social/queue/${encodeURIComponent(clientSlug)}?${params.toString()}`,
  );
}

export async function suggestSocialReply(
  payload: SocialReplySuggestPayload,
): Promise<SocialReplySuggestResponse> {
  return await request<SocialReplySuggestResponse>("/api/social/reply/suggest", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function publishSocialReply(
  payload: SocialReplyPublishPayload,
): Promise<SocialReplyPublishResponse> {
  return await request<SocialReplyPublishResponse>("/api/social/reply/publish", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function toggleSocialReplyModule(
  clientSlug: string,
  enabled: boolean,
  draftsEnabled: boolean = true,
): Promise<{ client_slug: string; reply_enabled: boolean; drafts_enabled: boolean; updated_at: string }> {
  return await request("/api/social/reply/toggle-module", {
    method: "POST",
    body: JSON.stringify({
      client_slug: clientSlug,
      enabled,
      drafts_enabled: draftsEnabled,
    }),
  });
}

export async function fetchSocialReplyAudit(
  clientSlug: string,
  limit: number = 50,
): Promise<{ client_slug: string; audit_records: any[] }> {
  return await request(`/api/social/reply/audit/${encodeURIComponent(clientSlug)}?limit=${limit}`);
}

export interface CreateHitoPayload {
  id_item: string;
  client_slug: string;
  dominio: string;
  titulo: string;
  comentario?: string;
  executed_at?: string;
}

export async function createClientHito(payload: CreateHitoPayload): Promise<{ ok: boolean }> {
  return await request<{ ok: boolean }>("/api/audit/action/execute", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export interface DeleteHitoPayload {
  id_item: string;
  client_slug: string;
}

export async function deleteClientHito(payload: DeleteHitoPayload): Promise<{ ok: boolean }> {
  return await request<{ ok: boolean }>("/api/audit/action/delete", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchClientHitos(clientSlug: string): Promise<any[]> {
  return await request<any[]>(`/api/client-hitos/${encodeURIComponent(clientSlug)}`);
}


export async function fetchSocialMensajes(clientSlug: string): Promise<SocialMensajesPayload> {
  return await request<SocialMensajesPayload>(`/api/social/mensajes/${encodeURIComponent(clientSlug)}`);
}

export async function fetchSocialMensajesLista(
  clientSlug: string,
  platform: string,
  filtro: SocialMensajesFiltro,
  page: number,
): Promise<SocialMensajesListaPayload> {
  const params = new URLSearchParams({ platform, filtro, page: String(page) });
  return await request<SocialMensajesListaPayload>(
    `/api/social/mensajes/${encodeURIComponent(clientSlug)}/conversaciones?${params.toString()}`,
  );
}

export async function fetchSocialMensajesHilo(
  clientSlug: string,
  platform: string,
  conversationId: string,
): Promise<SocialMensajesHilo> {
  return await request<SocialMensajesHilo>(
    `/api/social/mensajes/${encodeURIComponent(clientSlug)}/conversaciones/${encodeURIComponent(conversationId)}?platform=${encodeURIComponent(platform)}`,
  );
}

export async function fetchSocialMensajesPanorama(clientSlug: string, platform: string): Promise<SocialMensajesPanorama> {
  return await request<SocialMensajesPanorama>(
    `/api/social/mensajes/${encodeURIComponent(clientSlug)}/panorama?platform=${encodeURIComponent(platform)}`,
  );
}

async function publico<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: "include", headers: { "Content-Type": "application/json" }, ...init });
  const cuerpo = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof cuerpo.detail === "string" ? cuerpo.detail : "No fue posible completar la solicitud.");
  return cuerpo as T;
}

export async function verInvitacion(token: string): Promise<{ email: string; client_slug: string }> {
  return await publico(`/api/portal/invitacion?token=${encodeURIComponent(token)}`);
}

export async function aceptarInvitacion(token: string, password: string, nombre: string): Promise<{ success: boolean }> {
  return await publico("/api/portal/invitacion/aceptar", { method: "POST", body: JSON.stringify({ token, password, nombre }) });
}

export async function fetchClientsLite(): Promise<Array<{ clientSlug: string; displayName: string; objective?: ClientObjective }>> {
  return (await request<{ clients: Array<{ clientSlug: string; displayName: string; objective?: ClientObjective }> }>("/api/clients-lite")).clients;
}

export async function fetchSocialYoutube(clientSlug: string): Promise<SocialYoutubePayload> {
  return await request<SocialYoutubePayload>(`/api/client/${encodeURIComponent(clientSlug)}/youtube`);
}

export type GoldenTipo = "comentario" | "dm";

export interface GoldenItem {
  golden_id: string;
  tipo: GoldenTipo;
  plataforma: string;
  texto: string;
  estrellas: number | null;
  etiquetado: boolean;
  nota: string | null;
  /** Publicación (o ficha de Google) donde ocurrió el caso; no incluye lo que dijo el clasificador. */
  post_url?: string | null;
  post_titulo?: string | null;
  [campo: string]: unknown;
}

export interface GoldenOpciones {
  campos: Record<GoldenTipo, string[]>;
  opciones: Record<string, string[]>;
}

export interface GoldenGrupo {
  client_slug: string;
  plataforma: string;
  tipo: GoldenTipo;
  total: number;
  etiquetados: number;
}

export interface GoldenAcuerdo {
  client_slug: string;
  plataforma: string;
  tipo: GoldenTipo;
  etiquetados: number;
  acuerdo_polaridad: number | null;
  acuerdo_acto: number | null;
  acuerdo_aspecto: number | null;
  acuerdo_lead: number | null;
  acuerdo_responder: number | null;
}

export const goldenOpciones = (slug: string) =>
  request<GoldenOpciones>(`/api/golden/opciones?client_slug=${encodeURIComponent(slug)}`);
export const goldenGrupos = () => request<GoldenGrupo[]>("/api/golden/grupos");
export const goldenResumen = () => request<GoldenAcuerdo[]>("/api/golden/resumen");
export const goldenItems = (slug: string, plataforma: string, tipo: GoldenTipo) =>
  request<GoldenItem[]>(
    `/api/golden/items?client_slug=${encodeURIComponent(slug)}&plataforma=${plataforma}&tipo=${tipo}`,
  );
export const goldenMuestra = (client_slug: string, plataforma: string, tipo: GoldenTipo, cantidad = 40) =>
  request<{ agregados: number }>("/api/golden/muestra", {
    method: "POST",
    body: JSON.stringify({ client_slug, plataforma, tipo, cantidad }),
  });
export const goldenEtiquetar = (id: string, etiquetas: Record<string, string>) =>
  request<{ ok: boolean }>(`/api/golden/items/${id}`, { method: "PUT", body: JSON.stringify(etiquetas) });
export const goldenExportUrl = (tipo: GoldenTipo, slug?: string) =>
  `/api/golden/export?tipo=${tipo}${slug ? `&client_slug=${encodeURIComponent(slug)}` : ""}`;


import type { AdsTrackingOverviewPayload, AdsTrackingClientPayload } from "./types";

export async function fetchAdsTrackingOverview(): Promise<AdsTrackingOverviewPayload> {
  return request<AdsTrackingOverviewPayload>("/api/ads-tracking/overview");
}

export async function fetchAdsTrackingClient(clientSlug: string): Promise<AdsTrackingClientPayload> {
  return request<AdsTrackingClientPayload>(`/api/ads-tracking/client/${encodeURIComponent(clientSlug)}`);
}
